// @flow

/**
 * Agent Message Body Component
 * Renders agent message with events timeline, download actions, and markdown text
 */

import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import EventTimeline from './EventTimeline';
import ReasonStack from './ReasonStack';
import DownloadActions from './DownloadActions';
import MarkdownMessage from './MarkdownMessage';
import FeedbackRating from './FeedbackRating';
import { DataDisplayContext } from './data-display/DataDisplayContext';
import { AnswerToggleContext } from './answer-blocks';
import { needsTopSpacer } from '../utils/formatters';
import { usePromptChatState } from '../context';

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;
  ${props => props.$showBorder ? `
    padding-top: 0.75rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  ` : ''}
`;

const AgentMessageBody = ({ message, isActive, activeStreamMessageId }) => {
  const { sessionId } = usePromptChatState();
  const events = Array.isArray(message.events) ? message.events : [];
  const hasText = Boolean(message.text);
  const hasTraceId = Boolean(message.traceId);
  const isIntro = Boolean(message.isIntro);
  const fromHistory = Boolean(message.fromHistory);

  // Provide dataDisplays context for DataDisplayTag components rendered within MarkdownMessage
  const dataDisplayContextValue = useMemo(() => ({
    dataDisplays: message.dataDisplays || {},
  }), [message.dataDisplays]);

  // Summary/detail toggle state (per message). Default collapsed, including for
  // fromHistory messages. exportMode is driven by DownloadActions during export.
  const [showDetail, setShowDetail] = useState(false);
  const [exportMode, setExportMode] = useState(false);

  // Determine if this message is currently streaming
  const isStreaming = isActive && activeStreamMessageId === message.id;

  // Provide summary/detail toggle context for AnswerSummary/AnswerDetail
  const answerToggleContextValue = useMemo(() => ({
    showDetail,
    setShowDetail,
    isMessageStreaming: isStreaming,
    exportMode,
  }), [showDetail, setShowDetail, isStreaming, exportMode]);

  // Determine if we should show the actions row:
  // - Show if there's downloadable text (not intro)
  // - OR show if there's a traceId and NOT from history (for rating)
  const showActionsRow = (hasText && !isIntro) || (hasTraceId && !fromHistory);
  
  // Show border only when rating is shown
  const showBorder = hasTraceId && !fromHistory;

  return (
    <AnswerToggleContext.Provider value={answerToggleContextValue}>
      <DataDisplayContext.Provider value={dataDisplayContextValue}>
        {/* Only show EventTimeline if NOT from history */}
        {!fromHistory && events.length > 0 && (
          <EventTimeline
            events={events}
            isActive={isActive}
            hasText={hasText}
          />
        )}
        {/* Running commentary for in-flight tool calls. Unmounted (instantly, no
            fade) the moment the answer starts streaming, so it never shifts the
            response mid-render. */}
        {!fromHistory && isActive && !hasText && events.length > 0 && (
          <ReasonStack events={events} />
        )}
        {hasText ? (
          <div style={
            events.length > 0 && !fromHistory && needsTopSpacer(message.text)
              ? { marginTop: '0.5rem' }
              : undefined
          }>
            <MarkdownMessage text={message.text} />
          </div>
        ) : null}
        {/* Actions row: downloads for any message with text, rating only if NOT fromHistory */}
        {showActionsRow ? (
          <ActionsRow $showBorder={showBorder}>
            {/* Downloads: show for any non-intro message with text (including fromHistory) */}
            {hasText && !isIntro ? (
              <DownloadActions
                markdownText={message.text || ''}
                isStreaming={isStreaming}
                dataDisplays={message.dataDisplays}
                onExportModeChange={setExportMode}
              />
            ) : null}
            {/* Rating: only show if NOT from history and has traceId */}
            {hasTraceId && !fromHistory ? (
              <FeedbackRating
                messageId={message.id}
                traceId={message.traceId}
                initialFeedback={message.feedback}
                inline
              />
            ) : null}
          </ActionsRow>
        ) : null}
      </DataDisplayContext.Provider>
    </AnswerToggleContext.Provider>
  );
};

export default AgentMessageBody;
